import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth/context";
import { enableNotifications } from "@/lib/firebase/messaging";
import { toast } from "@/components/shell/Toaster";

const STORAGE_DISMISSED = "kd_push_banner_dismissed_until";

/**
 * Banner zachęcający do włączenia push. Pokazuje się gdy:
 * - User zalogowany
 * - Notification.permission !== "granted" (default lub denied)
 * - Banner nie był odrzucony w ostatnich 7 dniach (localStorage)
 *
 * Bez kliknięcia "Włącz" notyfikacje fizycznie nie działają (Firebase
 * pushSend wraca sent:0 gdy brak FCM tokena dla usera).
 */
export function PushEnableBanner() {
  const { firebaseUser, userDoc } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    const refresh = () => setPermission(Notification.permission);
    refresh();
    // Browser nie ma onChange dla permission — focus listener łapie powrót
    // z ustawień systemu lub po włączeniu w innej zakładce / Settings.
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    const dismissedUntil = Number(localStorage.getItem(STORAGE_DISMISSED) ?? 0);
    if (dismissedUntil > Date.now()) setDismissed(true);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  if (!firebaseUser || !userDoc) return null;
  if (permission === null) return null;
  if (permission === "granted") return null;
  if (dismissed) return null;

  const handleEnable = async () => {
    if (!userDoc.householdId) return;
    setBusy(true);
    try {
      await enableNotifications(userDoc.householdId, firebaseUser.uid);
      toast.success("Powiadomienia włączone 🔔");
      setPermission("granted");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Błąd";
      if (msg.includes("Odmowa")) {
        // User explicit denied — schowaj banner na 7 dni
        const week = Date.now() + 7 * 24 * 3600 * 1000;
        localStorage.setItem(STORAGE_DISMISSED, String(week));
        setDismissed(true);
        toast(
          "Włącz w ustawieniach systemu → Powiadomienia → Sianko",
        );
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    const week = Date.now() + 7 * 24 * 3600 * 1000;
    localStorage.setItem(STORAGE_DISMISSED, String(week));
    setDismissed(true);
  };

  return (
    <div className="rounded-[14px] border border-accent/30 bg-accent-soft px-4 py-3 text-sm relative">
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-muted hover:text-ink"
        aria-label="Schowaj"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Bell className="h-5 w-5 shrink-0 text-accent mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink">Włącz powiadomienia</p>
          <p className="mt-0.5 text-xs text-muted">
            Bez nich nie zobaczysz nowych zadań, zatwierdzeń ani wypłat
            dopóki nie otworzysz aplikacji.
          </p>
          <div className="mt-3">
            <Button size="sm" variant="accent" onClick={handleEnable} disabled={busy}>
              <Bell className="h-3.5 w-3.5" />
              {busy ? "Włączam…" : "Włącz teraz"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
